import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import { DeviceConnectionTypeEnum, DeviceConnectivity } from '@/constants/enums';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import ConnectivityDisconnectedIcon from '@/comp/common/icons/ConnectivityDisconnectedIcon';
import WindIcon from '@/comp/common/icons/WindIcon';

import { DesiccantExtendedActivityEnum } from './enums';

const { useToken } = theme;

interface DesiccantDryerConnectivityIconProps {
  connectivity: DeviceConnectivity;
  connectionType: DeviceConnectionTypeEnum;
  dryerExtendedActivity: DesiccantExtendedActivityEnum;
}

export const DesiccantDryerConnectivityIcon = ({
  connectivity,
  connectionType,
  dryerExtendedActivity,
}: DesiccantDryerConnectivityIconProps) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { iconStrokeColor, iconFillColor, tooltip } =
    getConnectivityIconColorAndTooltip(connectivity, connectionType, dryerExtendedActivity, token) ?? {};

  let shape;
  switch (connectivity) {
    case DeviceConnectivity.Connected:
    case DeviceConnectivity.NoState:
      shape = <WindIcon iconFillColor={iconFillColor} iconStrokeColor={iconStrokeColor} />;
      break;
    case DeviceConnectivity.Disconnected:
      shape = <ConnectivityDisconnectedIcon width={18} height={18} />;
      break;

    default:
      shape = null;
  }

  return (
    <div className="connectivity-icon-wrapper">
      <ConditionalToolTip condition={shape != null} content={t(tooltip)}>
        {shape}
      </ConditionalToolTip>
    </div>
  );
};

const getConnectivityIconColorAndTooltip = (connectivity, connectionType, extendedActivity, token) => {
  if (connectivity === DeviceConnectivity.NoState)
    return { iconStrokeColor: token.colorPrimary, iconFillColor: token.colorPrimaryBg, tooltip: 'No State' };
  if (connectivity === DeviceConnectivity.Disconnected) return { tooltip: 'Disconnected' };
  if (connectivity === DeviceConnectivity.Connected) {
    if (connectionType === DeviceConnectionTypeEnum['N/A'])
      return { iconStrokeColor: token.colorPrimary, iconFillColor: token.colorPrimaryBg, tooltip: 'drying' };

    if (connectionType === DeviceConnectionTypeEnum.IO)
      return { iconStrokeColor: token.colorSuccess, iconFillColor: token.colorSuccessBg, tooltip: 'drying' };

    if (connectionType === DeviceConnectionTypeEnum.Controller) {
      if (extendedActivity === DesiccantExtendedActivityEnum.On)
        return { iconStrokeColor: token.colorPrimary, iconFillColor: token.colorPrimaryBg, tooltip: 'drying' };
      if (extendedActivity === DesiccantExtendedActivityEnum.Off)
        return {
          iconStrokeColor: token.colorBgTextActive,
          iconFillColor: token.colorBgContainerDisabled,
          tooltip: 'Off',
        };
    }
  }
};
